import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../utils/formatDate';
import {
  FileText, RefreshCw, Eye, Download, Trash2, Upload,
  ChevronLeft, ChevronRight, Zap, Building2,
  CheckCircle, AlertCircle, Clock, X, File, Search
} from 'lucide-react';
import { multiAmcGetWorkOrders, multiAmcExtract, multiAmcDeleteWorkOrder, multiAmcGetAmcs } from '../api';
import './MultiAMCWorkOrders.css';

// Custom Dropdown Component
const CustomDropdown = ({ value, onChange, options, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const selectedOption = options.find(opt => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  return (
    <div className="custom-dropdown" ref={dropdownRef}>
      <button className="dropdown-trigger" onClick={() => setIsOpen(!isOpen)}>
        <span>{selectedOption?.label || placeholder}</span>
        <svg className={`dropdown-arrow ${isOpen ? 'open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </button>
      {isOpen && (
        <div className="dropdown-menu">
          {options.map(option => (
            <button
              key={option.value}
              className={`dropdown-option ${value === option.value ? 'selected' : ''}`}
              onClick={() => { onChange(option.value); setIsOpen(false); }}
            >
              {option.label}
              {value === option.value && <span className="check-mark">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Upload Modal
const UploadModal = ({ isOpen, onClose, onUploadComplete, amcs }) => {
  const [files, setFiles] = useState([]);
  const [selectedAmc, setSelectedAmc] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({});
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    addFiles(selectedFiles);
  };

  const addFiles = (newFiles) => {
    const validFiles = newFiles.filter(f =>
      f.name.toLowerCase().endsWith('.pdf') ||
      f.name.toLowerCase().endsWith('.tiff') ||
      f.name.toLowerCase().endsWith('.tif') ||
      f.name.toLowerCase().endsWith('.jpg') ||
      f.name.toLowerCase().endsWith('.jpeg') ||
      f.name.toLowerCase().endsWith('.png')
    );
    setFiles(prev => [...prev, ...validFiles]);
  };

  const handleUploadAll = async () => {
    if (files.length === 0 || !selectedAmc) return;

    setUploading(true);
    const results = [];

    for (const file of files) {
      setUploadStatus(prev => ({ ...prev, [file.name]: 'uploading' }));
      try {
        const result = await multiAmcExtract(file, selectedAmc);
        setUploadStatus(prev => ({ ...prev, [file.name]: result.success ? 'success' : 'error' }));
        results.push({ filename: file.name, ...result });
      } catch (err) {
        setUploadStatus(prev => ({ ...prev, [file.name]: 'error' }));
        results.push({ filename: file.name, success: false, error: err.message });
      }
    }

    setUploading(false);
    if (onUploadComplete) onUploadComplete(results);
  };

  const handleClose = () => {
    setFiles([]);
    setSelectedAmc('');
    setUploadStatus({});
    onClose();
  };

  if (!isOpen) return null;

  const allUploaded = files.length > 0 && files.every(f => ['success', 'error'].includes(uploadStatus[f.name]));

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="upload-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3><Upload size={20} /> Upload & Extract</h3>
          <button className="close-btn" onClick={handleClose}><X size={20} /></button>
        </div>

        <div className="modal-body">
          <div className="form-group">
            <label>Select AMC *</label>
            <select value={selectedAmc} onChange={e => setSelectedAmc(e.target.value)} disabled={uploading}>
              <option value="">-- Select AMC --</option>
              {amcs.map(amc => (
                <option key={amc.code} value={amc.code}>{amc.name}</option>
              ))}
            </select>
          </div>

          <div
            className="drop-zone"
            onClick={() => !uploading && fileInputRef.current?.click()}
          >
            <Upload size={40} />
            <p>Drop files here or click to browse</p>
            <span>PDF, TIFF, JPG, PNG</span>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.tiff,.tif,.jpg,.jpeg,.png"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />

          {files.length > 0 && (
            <div className="file-list">
              {files.map(file => (
                <div key={file.name} className={`file-item ${uploadStatus[file.name] || 'pending'}`}>
                  <File size={16} />
                  <span>{file.name}</span>
                  {uploadStatus[file.name] === 'uploading' && <RefreshCw size={16} className="spin" />}
                  {uploadStatus[file.name] === 'success' && <CheckCircle size={16} style={{ color: '#22c55e' }} />}
                  {uploadStatus[file.name] === 'error' && <AlertCircle size={16} style={{ color: '#ef4444' }} />}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-cancel" onClick={handleClose}>
            {allUploaded ? 'Close' : 'Cancel'}
          </button>
          {!allUploaded && (
            <button
              className="btn-extract"
              onClick={handleUploadAll}
              disabled={files.length === 0 || !selectedAmc || uploading}
            >
              {uploading ? <><RefreshCw size={16} className="spin" /> Processing...</> : <><Zap size={16} /> Extract All</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Component
const MultiAMCWorkOrders = () => {
  const navigate = useNavigate();
  const [workOrders, setWorkOrders] = useState([]);
  const [amcs, setAmcs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1, limit: 20, skip: 0 });
  const [statusFilter, setStatusFilter] = useState('all');
  const [amcFilter, setAmcFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'pending', label: 'Pending' },
    { value: 'processing', label: 'Processing' },
    { value: 'completed', label: 'Completed' },
    { value: 'failed', label: 'Failed' },
  ];

  const fetchWorkOrders = useCallback(async (skip = 0) => {
    setLoading(true);
    setError(null);
    try {
      const response = await multiAmcGetWorkOrders({
        skip,
        limit: pagination.limit,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        amc_code: amcFilter || undefined,
        search: searchTerm || undefined,
      });

      if (response.success) {
        setWorkOrders(response.data || []);
        setPagination(response.pagination || { total: 0, page: 1, pages: 1, limit: 20, skip: 0 });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, statusFilter, amcFilter, searchTerm]);

  useEffect(() => {
    multiAmcGetAmcs('enabled').then(res => {
      if (res.success) setAmcs(res.amcs || []);
    });
  }, []);

  useEffect(() => {
    fetchWorkOrders(0);
  }, [statusFilter, amcFilter]);

  const handleSearch = () => {
    fetchWorkOrders(0);
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this work order?')) return;
    try {
      await multiAmcDeleteWorkOrder(id);
      fetchWorkOrders(pagination.skip);
    } catch (err) {
      setError(err.message);
    }
  };

  const getStatusClass = (status) => ({
    completed: 'status-completed',
    processing: 'status-processing',
    pending: 'status-pending',
    failed: 'status-failed',
  }[status] || 'status-pending');

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle size={12} />;
      case 'processing': return <RefreshCw size={12} className="spin" />;
      case 'failed': return <AlertCircle size={12} />;
      default: return <Clock size={12} />;
    }
  };

  return (
    <div className="multi-amc-work-orders">
      <div className="page-header">
        <div className="header-info">
          <h1><FileText size={28} /> Work Orders</h1>
          <p>Multi-AMC Extraction History</p>
        </div>
        <div className="header-actions">
          <button className="btn-upload" onClick={() => setShowUploadModal(true)}>
            <Upload size={18} /> Upload & Extract
          </button>
        </div>
      </div>

      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadComplete={() => { setShowUploadModal(false); fetchWorkOrders(0); }}
        amcs={amcs}
      />

      {error && (
        <div className="error-banner">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="filters-row">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search by ID or barcode..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
          />
        </div>

        <CustomDropdown
          value={statusFilter}
          onChange={setStatusFilter}
          options={statusOptions}
          placeholder="Status"
        />

        <select value={amcFilter} onChange={e => setAmcFilter(e.target.value)} className="amc-filter">
          <option value="">All AMCs</option>
          {amcs.map(amc => (
            <option key={amc.code} value={amc.code}>{amc.code}</option>
          ))}
        </select>

        <button className="btn-refresh" onClick={() => fetchWorkOrders(pagination.skip)}>
          <RefreshCw size={16} className={loading ? 'spin' : ''} /> Refresh
        </button>
      </div>

      <div className="table-container">
        <table className="work-orders-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>AMC</th>
              <th>Form Type</th>
              <th>Status</th>
              <th>Confidence</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="loading-cell"><RefreshCw size={20} className="spin" /> Loading...</td></tr>
            ) : workOrders.length === 0 ? (
              <tr><td colSpan="7" className="empty-cell">No work orders found</td></tr>
            ) : (
              workOrders.map(wo => (
                <tr key={wo.id} onClick={() => navigate(`/multi-amc/work-orders/${wo.id}`)}>
                  <td className="id-cell">{wo.id?.substring(0, 10)}...</td>
                  <td>
                    <span className="amc-badge">{wo.amc_code}</span>
                  </td>
                  <td className="form-type-cell">{wo.form_type?.replace('_', ' ')}</td>
                  <td>
                    <span className={`status-badge ${getStatusClass(wo.status)}`}>
                      {getStatusIcon(wo.status)} {wo.status}
                    </span>
                  </td>
                  <td>{wo.confidence ? `${(wo.confidence * 100).toFixed(0)}%` : '-'}</td>
                  <td>{formatDate(wo.created_at)}</td>
                  <td className="actions-cell">
                    <button className="action-btn view" title="View" onClick={e => { e.stopPropagation(); navigate(`/multi-amc/work-orders/${wo.id}`); }}>
                      <Eye size={16} />
                    </button>
                    <button className="action-btn delete" title="Delete" onClick={e => handleDelete(wo.id, e)}>
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination.pages > 1 && (
        <div className="pagination">
          <span>Page {pagination.page} of {pagination.pages} ({pagination.total} total)</span>
          <div className="pagination-controls">
            <button onClick={() => fetchWorkOrders(Math.max(0, pagination.skip - pagination.limit))} disabled={pagination.page <= 1}>
              <ChevronLeft size={16} /> Prev
            </button>
            <button onClick={() => fetchWorkOrders(pagination.skip + pagination.limit)} disabled={pagination.page >= pagination.pages}>
              Next <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiAMCWorkOrders;
